import React, { useState, useRef, useEffect } from 'react';
import { motion, Reorder } from 'framer-motion';
import { useTranslation } from '../i18n/useTranslation';
import { WardrobeItem } from '../types';
import { TrashIcon, SaveIcon, RefreshCwIcon, ShuffleIcon, UserIcon } from './icons';
import { useDynamicStyle } from '../hooks/useDynamicStyle';

interface StyleSandboxProps {
    wardrobe: WardrobeItem[];
    userProfile: any;
    onSaveOutfit: (items: SandboxItem[]) => void;
    currentUserId: string;
    onApplyToModel?: (items: SandboxItem[]) => void;
    baseModelImageUrl?: string | null;
    initialItems?: SandboxItem[];
    // New prop for external injection
    injectItem?: { item: WardrobeItem; timestamp: number } | null;
}

export interface SandboxItem extends WardrobeItem {
    instanceId: string;
    x: number;
    y: number;
    rotation: number;
    scale: number;
    zIndex: number;
}

export const StyleSandbox: React.FC<StyleSandboxProps> = ({ wardrobe, userProfile, onSaveOutfit, currentUserId, onApplyToModel, baseModelImageUrl, initialItems = [], injectItem }) => {
    const { t } = useTranslation();
    const [sandboxItems, setSandboxItems] = useState<SandboxItem[]>(initialItems);
    const [selectedItemId, setSelectedItemId] = useState<string | null>(null);
    const canvasRef = useRef<HTMLDivElement>(null);

    // Sync with initialItems if mode changes back and forth
    useEffect(() => {
        if (initialItems.length > 0) {
            setSandboxItems(initialItems);
        }
    }, [initialItems]);

    // Handle external injection
    useEffect(() => {
        if (injectItem) {
            handleAddToSandbox(injectItem.item);
        }
    }, [injectItem]);

    // Add item to sandbox (center of screen)
    const handleAddToSandbox = (item: WardrobeItem) => {
        const newItem: SandboxItem = {
            ...item,
            instanceId: `${item.id}-${Date.now()}`,
            x: 50, // Percent
            y: 50, // Percent
            rotation: (Math.random() - 0.5) * 20, // Random tilt
            scale: 1,
            zIndex: sandboxItems.length + 1,
        };
        setSandboxItems([...sandboxItems, newItem]);
    };

    // Update item position (simplified for now, ideally needs drag constraints)
    const updateItemPosition = (id: string, x: number, y: number) => {
        setSandboxItems(prev => prev.map(item =>
            item.instanceId === id ? { ...item, x, y } : item
        ));
    };

    const handleRemoveItem = (id: string) => {
        setSandboxItems(prev => prev.filter(item => item.instanceId !== id));
        setSelectedItemId(null);
    };

    const handleMagicShuffle = () => {
        setSandboxItems(prev => prev.map(item => ({
            ...item,
            x: 20 + Math.random() * 60,
            y: 20 + Math.random() * 60,
            rotation: (Math.random() - 0.5) * 45,
            scale: 0.8 + Math.random() * 0.4
        })));
    };

    const handleClearCanvas = () => {
        if (window.confirm(t('sandbox_clear_confirm') || "Clear canvas?")) {
            setSandboxItems([]);
        }
    };

    return (
        <div className="flex h-full bg-gray-50 dark:bg-gray-900 overflow-hidden">
            {/* Center: Canvas (Now Full Width) */}
            <div className="flex-grow relative overflow-hidden bg-[url('/patterns/grid.png')] bg-repeat" ref={canvasRef}>
                {/* Base Model Reference Background */}
                {baseModelImageUrl && (
                    <div className="absolute inset-0 flex items-center justify-center opacity-20 pointer-events-none z-0">
                        <img
                            src={baseModelImageUrl}
                            alt="Reference Model"
                            className="h-full w-auto object-contain p-12 grayscale"
                        />
                    </div>
                )}

                {/* Toolbar */}
                <div className="absolute bottom-6 sm:top-4 left-1/2 -translate-x-1/2 bg-white/90 dark:bg-gray-800/90 backdrop-blur rounded-full shadow-xl p-1.5 sm:p-2 flex gap-2 sm:gap-4 z-30 border border-gray-200 dark:border-gray-700">
                    <button onClick={handleMagicShuffle} className="p-2 hover:bg-indigo-50 dark:hover:bg-indigo-900/50 rounded-full text-indigo-600 transition-colors" title={t('sandbox_magic_shuffle')}>
                        <ShuffleIcon className="w-5 h-5" />
                    </button>
                    <button onClick={() => onSaveOutfit(sandboxItems)} className="p-2 hover:bg-green-50 dark:hover:bg-green-900/50 rounded-full text-green-600 transition-colors" title={t('sandbox_save')}>
                        <SaveIcon className="w-5 h-5" />
                    </button>
                    <button onClick={handleClearCanvas} className="p-2 hover:bg-red-50 dark:hover:bg-red-900/50 rounded-full text-red-600 transition-colors" title={t('sandbox_clear')}>
                        <TrashIcon className="w-5 h-5" />
                    </button>
                    {onApplyToModel && (
                        <button
                            onClick={() => onApplyToModel(sandboxItems)}
                            className="p-1 px-3 sm:p-2 sm:px-4 ml-1 sm:ml-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-full shadow-md flex items-center gap-1.5 sm:gap-2 transition-all active:scale-95"
                            title={t('sandbox_apply_to_model', 'Try on Model')}
                        >
                            <UserIcon className="w-3.5 h-3.5 sm:w-4 h-4" />
                            <span className="text-xs font-bold">{t('sandbox_apply_to_model', 'Try on Model')}</span>
                        </button>
                    )}
                </div>

                {/* Items */}
                {sandboxItems.map((item) => (
                    <DraggableItem
                        key={item.instanceId}
                        item={item}
                        isSelected={selectedItemId === item.instanceId}
                        onSelect={() => setSelectedItemId(item.instanceId)}
                        onUpdate={(updates) => setSandboxItems(prev => prev.map(p => p.instanceId === item.instanceId ? { ...p, ...updates } : p))}
                        onRemove={() => handleRemoveItem(item.instanceId)}
                    />
                ))}

                {sandboxItems.length === 0 && (
                    <div className="absolute inset-0 flex items-center justify-center text-gray-400 pointer-events-none">
                        <p className="text-xl font-medium">{t('sandbox_empty_state') || "Drag items here to create your look"}</p>
                    </div>
                )}
            </div>
        </div>
    );
};

const DraggableItem: React.FC<{
    item: SandboxItem;
    isSelected: boolean;
    onSelect: () => void;
    onUpdate: (updates: Partial<SandboxItem>) => void;
    onRemove: () => void;
}> = ({ item, isSelected, onSelect, onUpdate, onRemove }) => {
    const { t } = useTranslation();
    // This allows dynamic CSS variables for positioning if needed, 
    // but framer-motion 'drag' handles the immediate interaction better.
    // We update the state onDragEnd to persist.

    return (
        <motion.div
            drag
            dragMomentum={false}
            initial={{ opacity: 0, scale: 0.5, x: 0, y: 0 }}
            animate={{
                opacity: 1,
                scale: isSelected ? item.scale * 1.05 : item.scale,
                rotate: item.rotation,
                zIndex: isSelected ? 100 : item.zIndex
            }}
            whileDrag={{ scale: 1.1, zIndex: 101, cursor: 'grabbing' }}
            onDragEnd={(_, info) => {
                // Here we would ideally calculate percentage position relative to parent
                // For this MVP, we rely on framer-motion's visual preservation
            }}
            onClick={(e) => {
                e.stopPropagation();
                onSelect();
            }}
            className={`absolute cursor-grab ${isSelected ? 'ring-2 ring-indigo-500 rounded-lg' : ''}`}
            style={{
                left: `${item.x}%`,
                top: `${item.y}%`,
                touchAction: 'none'
            }}
        >
            <div className="relative group">
                <img
                    src={item.url}
                    alt={item.nameKey}
                    className="w-32 sm:w-48 h-auto object-contain drop-shadow-xl pointer-events-none"
                />

                {isSelected && (
                    <div className="absolute -top-4 -right-4 flex gap-1">
                        <button
                            onClick={(e) => { e.stopPropagation(); onRemove(); }}
                            className="absolute -top-3 -right-3 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity shadow-sm"
                            title={t('sandbox_remove_item') || "Remove Item"}
                        >
                            <TrashIcon className="w-4 h-4" />
                        </button>
                    </div>
                )}
            </div>
        </motion.div>
    );
};
